import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid'
import { ListChecksIcon } from '@phosphor-icons/react'
import { TOOLBAR_CONTAINER } from 'app/components/CompactLabel'
import { Column, ColumnDef, Table, VisibilityState } from '@tanstack/react-table'
import classNames from 'classnames'
import { Fragment, ReactElement, ReactNode, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search as SearchIcon } from 'iconoir-react'
import { Search } from '../Search'
import Spinner from '../Spinner'
import { TableFiltersView as TableFilters } from 'app/components/ui/table-filters/table-filters'
import type { FixedRules } from 'app/types/filters'
import TableColumnSelector from './TableColumnSelector'
import { TableColumnLinesToggle } from './TableColumnLinesToggle'
import { BulkActionBar } from './bulkActions/BulkActionBar'
import { useTableBulkSelection } from './bulkActions/useTableBulkSelection'
import type { BulkActionsConfig } from './bulkActions/types'
import { Button } from 'app/components/ui/button'
import { DataTable } from 'app/components/ui/data-table'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from 'app/components/ui/sheet'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { useHandleSearchParams } from 'app/utils'
import { FilterableColumn, FilterObject } from 'app/filter.utils'
import { buildClearedFilterParams } from 'app/lib/clearFilterParams'
import { useHasActiveFilters } from 'app/components/ui/table-filters/use-has-active-filters'

export type TableLayoutComponentProps<T> = {
  table: Table<T>
  columns: ColumnDef<T, any>[]
  filterableColumns?: FilterableColumn[]
  fixedRules?: FixedRules
  defaultJoin: `and` | `or`
  hideControls: boolean
  extraButtons?: (columns: ColumnDef<T, any>[], columnVisibility: VisibilityState) => ReactElement[]
  /** The very end of the toolbar row, outside the phone's "More" sheet. See TableLayout. */
  /** Leave column lines and the column editor's button out of the toolbar. See TableLayout. */
  viewToolsInSettings?: boolean
  trailingControls?: ReactNode
  drawer: ReactElement | null
  initialFilters?: FilterObject[]
  columnVisibility: VisibilityState
  defaultColumnVisibility: Record<string, boolean>
  initialColumnOrder: string[]
  sortOrder: { columnId?: string; order?: `asc` | `desc` }
  visibilityReady: boolean
  loading: boolean
  tableWrapperRef: React.RefObject<HTMLDivElement | null>
  toggleSort: (column: Column<T, unknown>) => void
  onScroll: (e: any) => void
  onResetSizing: () => void
  onAutoFitColumn: (columnId: string, width: number) => void
  pathname: string
  tab: string | null
  edgePadded?: boolean
  bulkActions?: BulkActionsConfig<T>
  selectionMode?: boolean
  toggleSelectionMode?: () => void
  exitSelectionMode?: () => void
  /** Shown inside the table frame when there are no rows and nothing is filtered. */
  emptyState?: ReactNode
  /**
   * Set when the page keeps its own filter state outside the URL (e.g. a local
   * SegmentedControl), which `hasNarrowed` cannot see on its own. OR'd into the
   * URL-derived check so those pages still get the "No matches" state instead
   * of the caller's onboarding `emptyState` when narrowed.
   */
  narrowed?: boolean
}

export function TableLayoutComponent<T>({
  table,
  columns,
  filterableColumns,
  fixedRules,
  defaultJoin,
  hideControls,
  extraButtons,
  trailingControls,
  viewToolsInSettings,
  drawer,
  initialFilters,
  columnVisibility,
  defaultColumnVisibility,
  initialColumnOrder,
  sortOrder,
  visibilityReady,
  loading,
  tableWrapperRef,
  toggleSort,
  onScroll,
  onResetSizing,
  onAutoFitColumn,
  pathname,
  tab,
  edgePadded,
  bulkActions,
  selectionMode,
  toggleSelectionMode,
  exitSelectionMode,
  emptyState,
  narrowed,
}: TableLayoutComponentProps<T>) {
  const isMobile = useIsMobile()
  const bulkSelection = useTableBulkSelection(table)
  const [moreOpen, setMoreOpen] = useState(false)
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const hasUrlFilters = useHasActiveFilters(filterableColumns, initialFilters)
  const searchQuery = searchParams.get(`query`) ?? ``
  const hasNarrowed = !!searchQuery || hasUrlFilters || !!narrowed

  // This shell drives search and filters entirely through the URL, so clearing
  // is one param write, shared with the other shell and with useTableFilters so
  // the three cannot drift apart. The URL-sync effect in useTableFilters
  // re-derives the filter chips from it. `query` is this shell's alone: it
  // belongs to the search box, not the filter contract.
  const clearAll = () => {
    handleSearchParams({
      query: ``,
      ...buildClearedFilterParams({ columns: filterableColumns, searchParams }),
    })
  }

  // Lesser-used table controls (bulk actions, export, column visibility). On
  // mobile they collapse into a "⋯ More" bottom sheet; on desktop they sit
  // inline in the toolbar.
  const bulkActionsButton = bulkActions ? (
    <Button
      variant={selectionMode ? `white` : `secondary`}
      size="sm"
      onClick={() => {
        toggleSelectionMode?.()
        setMoreOpen(false)
      }}
      title={selectionMode ? undefined : `Bulk actions`}
    >
      {selectionMode ? (
        `Done`
      ) : (
        <>
          <ListChecksIcon size={16} />
          {/* Measured against the whole row (`toolbar-row`), not the left half: this button
              is what the left half competes with for room. */}
          <span className="@max-3xl/toolbar-row:hidden">Bulk actions</span>
        </>
      )}
    </Button>
  ) : null

  const columnSelector = (
    <TableColumnSelector
      table={table}
      columnVisibility={columnVisibility}
      defaultColumnVisibility={defaultColumnVisibility}
      defaultColumnOrder={initialColumnOrder}
      activeSubpage={pathname === `/contacts` ? (tab ?? undefined) : undefined}
      onResetSizing={onResetSizing}
      hideTrigger={viewToolsInSettings}
    />
  )

  const utilityControls = (
    <>
      {bulkActionsButton}
      {extraButtons &&
        extraButtons(columns, columnVisibility).map((ExtraButton, index) => (
          <Fragment key={index}>{ExtraButton}</Fragment>
        ))}
      {!viewToolsInSettings && (
        <>
          <TableColumnLinesToggle />
          {columnSelector}
        </>
      )}
    </>
  )

  return (
    <div x-component-name="TableLayout" data-table className="relative flex h-full w-full flex-col overflow-hidden">
      {!hideControls ? (
        <div
          className={classNames(
            `@container/toolbar-row flex items-center justify-between gap-2`,
            edgePadded && `pr-1.5`,
          )}
        >
          {/* Wraps: 375px cannot hold Search and the filters beside the right half's controls,
              and a clipped control is unreachable. */}
          <div className={classNames(`flex min-w-0 flex-1 flex-wrap items-center gap-2`, TOOLBAR_CONTAINER)}>
            <Search />
            {filterableColumns && (
              <TableFilters
                columns={filterableColumns}
                fixedRules={fixedRules}
                defaultJoin={defaultJoin}
                initialFilters={initialFilters}
              />
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 md:items-end">
            {isMobile ? (
              <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
                <SheetTrigger asChild>
                  <Button variant="white" size="icon-sm" aria-label="More table options">
                    <EllipsisHorizontalIcon className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                {/* Auto-focusing the first control (Export) pops its focus-triggered tooltip on open. */}
                <SheetContent
                  side="bottom"
                  className="rounded-t-xl pb-[env(safe-area-inset-bottom)]"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <SheetHeader>
                    <SheetTitle>Table options</SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col items-start gap-3 p-4">{utilityControls}</div>
                </SheetContent>
              </Sheet>
            ) : (
              utilityControls
            )}
            {/* Mounted outside the phone's sheet, so a request from Settings always has an editor to open. */}
            {viewToolsInSettings && columnSelector}
            {trailingControls}
          </div>
        </div>
      ) : null}
      <div className="mt-6 flex h-full overflow-hidden">
        <div ref={tableWrapperRef} className="flex grow flex-col overflow-hidden">
          {visibilityReady &&
            (loading ? (
              <div className="my-5 grid w-full place-items-center">
                <Spinner size={10} />
              </div>
            ) : (
              <DataTable
                table={table}
                sortOrder={sortOrder}
                toggleSort={toggleSort}
                onScroll={onScroll}
                onAutoFitColumn={onAutoFitColumn}
                emptyState={
                  hasNarrowed ? (
                    <TableEmptyState
                      icon={<SearchIcon className="h-6 w-6" strokeWidth={1.5} />}
                      title="No matches"
                      description={
                        searchQuery ? `Nothing matches "${searchQuery}".` : `Nothing matches the current filters.`
                      }
                      action={
                        <Button variant="secondary" size="sm" onClick={clearAll}>
                          Clear search and filters
                        </Button>
                      }
                    />
                  ) : (
                    emptyState
                  )
                }
              />
            ))}
        </div>
      </div>
      {bulkActions && selectionMode && (
        <BulkActionBar selection={bulkSelection} config={bulkActions} onExit={exitSelectionMode ?? (() => {})} />
      )}
      {drawer}
    </div>
  )
}
